import React, { useEffect, useRef, useState } from 'react';
import { Box, FormGroup } from '@material-ui/core';
import { wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsPasswordInput from 'ui-elements/form-elements/FsPasswordInput';
import { isWeekPasswordRe } from 'ui-elements/form-elements/FsPasswordStrengthChecker/PasswordStrengthChecker.utils';

interface FsLoginFormProps {
  onSuccess?: () => void;
}

const FsResetPasswordForm: React.FC<FsLoginFormProps> = ({ onSuccess }) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const setNew = async (): Promise<void> => {
    setPending(true);
    await wait(1000);
    setPending(false);
    if (onSuccess) onSuccess();
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await setNew();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPasswordValid =
      !!newPassword.match(isWeekPasswordRe) &&
      newPassword === newPasswordRepeat;
    setIsFormValid(isDefaultFormValid && isPasswordValid);
  }, [newPassword, newPasswordRepeat]);

  return (
    <form onSubmit={handleSubmit} ref={formEl}>
      <FormGroup>
        <FsFormFullWidthContainer>
          <FsPasswordInput
            required
            fsVariant="outlined"
            name={defaultLocales.password.toLowerCase()}
            label={defaultLocales.password}
            type="password"
            value={newPassword}
            onChange={(e): void => {
              setNewPassword(e.target.value);
            }}
            fullWidth
            variant="outlined"
            showStrengthChecker
            showInfo
            autoFocus
          />
        </FsFormFullWidthContainer>

        <FsFormFullWidthContainer>
          <FsPasswordInput
            required
            fsVariant="outlined"
            name="password-repeat"
            label={defaultLocales.repeatPassword}
            type="password"
            value={newPasswordRepeat}
            onChange={(e): void => {
              setNewPasswordRepeat(e.target.value);
            }}
            fullWidth
            variant="outlined"
          />
        </FsFormFullWidthContainer>

        <Box>
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            disabled={!isFormValid}
            endIcon={<FsNextArrowIcon />}
            width={160}
            spaceBetween
          >
            {defaultLocales.update}
          </FsPendingButton>
        </Box>
      </FormGroup>
    </form>
  );
};

export default FsResetPasswordForm;
